<script>
import {mapActions, mapState} from "pinia";
import {authStore} from "@/store/authStore";
import {bankIdStore} from "@/store/bankIdStore";
import {userStore} from "@/store/userStore";
import {baseLoaderStore} from "@/store/baseLoaderStore";
import {helpers, required, url, minLength} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import i18n from "@/i18n";
import Dialog from "primevue/dialog";
import LoginView from "@/mobile/LoginView.vue";
import BankIdForm from "@/components/BankId/BankIdForm.vue";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";

export default {
  name: "BecomeManagerMobile",
  setup () {
    return { v$: useVuelidate() }
  },
  components: {
    Dialog,
    LoginView,
    BankIdForm,
    InputText,
    Textarea
  },
  data() {
    return {
      showIntroState: false,
      activeStep: 1,

      logoutModalState: false,

      //Form fields
      personalNumber: '',
      phoneNumber: '',
      aboutMe: '',
      applicationText: '',
      facebookProfile: '',
      instagramProfile: '',
      linkedinProfile: '',
      snapchatProfile: '',
    }
  },
  validations() {
    return {
      phoneNumber: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required)
      },
      applicationText: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required),
        minLength: helpers.withMessage(i18n.global.t('validations.minChars10'), minLength(10)),
        htmlTags: helpers.withMessage(i18n.global.t('validations.containTags'), val => /<(.|\n)*?>/g.test(val) === false)
      },
      aboutMe: {
        required: helpers.withMessage(i18n.global.t('validations.required'), required),
        minLength: helpers.withMessage(i18n.global.t('validations.minChars10'), minLength(10)),
        htmlTags: helpers.withMessage(i18n.global.t('validations.containTags'), val => /<(.|\n)*?>/g.test(val) === false)
      },
      facebookProfile: {
        url: helpers.withMessage(i18n.global.t('validations.enterValidUrl'), url),
      },
      linkedinProfile: {
        url: helpers.withMessage(i18n.global.t('validations.enterValidUrl'), url),
      },
      instagramProfile: {
        url: helpers.withMessage(i18n.global.t('validations.enterValidUrl'), url),
      },
      snapchatProfile: {
        url: helpers.withMessage(i18n.global.t('validations.enterValidUrl'), url),
      },
    }
  },
  computed: {
    ...mapState(authStore, [
      'userLoggedIn',
      'userInfo',
      'userIsManager',
      'userIsActiveManager',
      'getUserApplications',
      'userApplications',
      'sendApplicationAction',
      'activeProfileInfo'
    ]),
    ...mapState(bankIdStore, [
      'bankIdProfileData'
    ]),
    ...mapState(userStore, [
      'userEmails'
    ]),
    userHasNoAnsweredApplications() {
      return this.userApplications.filter((application)=> {
        return application.decision === null;
      }).length > 0;
    },
    bankIdAuthReady() {
      return this.bankIdProfileData && this.bankIdProfileData.lastLoginTime !== null;
    }
  },
  watch: {
    userLoggedIn: {
      handler() {
        this.checkUserIsAuth();
      }
    },
    showIntroState: {
      handler(val) {
        if (!val) {
          this.hideIntro();
        }
      }
    },
  },
  methods: {
    ...mapActions(bankIdStore, [
      'checkBankIdAuthSession',
      'getBankIdProfileData'
    ]),
    ...mapActions(authStore, [
      'logoutAction'
    ]),
    ...mapActions(baseLoaderStore, [
      'showBaseLoader',
      'hideBaseLoader'
    ]),
    setViewport() {
      const viewportWidth = 'device-width';
      const mvp = document.getElementById('metaviewport');
      mvp.setAttribute('content','user-scalable=no,width=' + viewportWidth);
    },
    showIntro(forceShow) {
      const hideBmIntroVideoState = sessionStorage.getItem("hideBmIntroVideo") === 'true';

      if (forceShow) {
        this.showIntroState = true;
        sessionStorage.setItem("hideBmIntroVideo", 'false');
      } else {
        this.showIntroState = !hideBmIntroVideoState;
      }
    },
    hideIntro() {
      this.showIntroState = false;
      sessionStorage.setItem("hideBmIntroVideo", 'true');
    },
    checkUserIsAuth() {
      this.getBankIdProfileData().then(()=> {
        if (this.userLoggedIn) {
          this.getUserApplications().then(()=> {
            if (this.userHasNoAnsweredApplications) {
              this.activeStep = 5;
            } else {
              if (!this.userIsManager || !this.userIsActiveManager) {
                if (this.bankIdAuthReady) {
                  this.activeStep = 4;
                } else {
                  this.checkBankIdAuthSession(()=> {
                    if (this.userLoggedIn) {
                      this.activeStep = 4;
                    } else {
                      this.activeStep = 1;
                    }
                  }, ()=> {
                    if (this.userLoggedIn) {
                      this.activeStep = 3;
                    } else {
                      this.activeStep = 1;
                    }
                  });
                }
              } else {
                this.activeStep = 5;
              }
            }
            this.showIntro();
          })
        } else {
          this.activeStep = 1;
          this.showIntro();
        }
      })
    },
    async sendApplication() {
      const isFormValid = await this.v$.$validate()
      const data = {
        type: "BECOME_MANAGER",
        phoneNumber: this.phoneNumber,
        aboutMe: this.aboutMe,
        applicationText: this.applicationText,
        facebookProfile: this.facebookProfile.length > 0 ? this.facebookProfile : null,
        instagramProfile: this.instagramProfile.length > 0 ? this.instagramProfile : null,
        linkedinProfile: this.linkedinProfile.length > 0 ? this.linkedinProfile : null,
        snapchatProfile: this.snapchatProfile.length > 0 ? this.snapchatProfile : null,
      };

      this.v$.$touch();
      if (isFormValid) {
        this.sendApplicationAction(data).then(()=> {
          this.activeStep = 5;
        })
      }
    },
    isNumber(evt) {
      evt = (evt) ? evt : window.event;
      var charCode = (evt.which) ? evt.which : evt.keyCode;
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    showLogoutConfirmDialog() {
      this.logoutModalState = true;
    },
    logout() {
      this.showBaseLoader();
      this.logoutAction().then(()=> {
        this.logoutModalState = false;
        this.activeStep = 1;
        this.hideBaseLoader();
      });
    },
    openLoginView() {
      this.activeStep = 2;
    },
    openSignUpView() {
      this.$router.push({ name: 'SignUpView' });
    }
  },
  mounted() {
    this.checkUserIsAuth();
    this.setViewport();

    window.parent.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideIntro();
      }
    });
  }
}
</script>

<template>
  <div v-if="activeStep === 1" class="wizard-view wizard-view--1">
    <div class="wizard-view-header">
      <a href="/" class="wizard-logo-icon"></a>
      <div class="start-intro-btn-wrap" @click="showIntro(true);">
        <font-awesome-icon :icon="['fas', 'play']" class="start-intro-btn-wrap__icon"/>
        {{$t('becomeManager.lifeAtPeoplePolitics')}}
      </div>
    </div>
    <div class="step-desc-section">
      <h3 class="step-title">{{$t('becomeManager.welcome.title')}}</h3>
      <p class="step-desc">{{$t('becomeManager.welcome.p1')}}</p>
      <p class="step-desc">{{$t('becomeManager.welcome.p2')}}</p>
      <p class="step-desc">{{$t('becomeManager.welcome.p3')}}</p>
    </div>
    <div class="form-group">
      <button type="button" class="login-btn" @click="openSignUpView()">
        {{$t('header.becomeMember')}}
      </button>
    </div>
    <div class="form-group">
      <button type="button" class="login-btn" @click="openLoginView()">
        {{$t('auth.login')}}
      </button>
    </div>
    <div class="login-step-bg"></div>
  </div>
  <div v-else-if="activeStep === 2" class="wizard-view wizard-view--1">
    <div class="wizard-view-header">
      <a href="/" class="wizard-logo-icon"></a>
      <div class="start-intro-btn-wrap" @click="showIntro(true);">
        <font-awesome-icon :icon="['fas', 'play']" class="start-intro-btn-wrap__icon"/>
        {{$t('becomeManager.lifeAtPeoplePolitics')}}
      </div>
    </div>
    <h3 class="step-title">{{$t('becomeManager.form.youNeedToBe')}}</h3>
    <login-view @on-login="checkUserIsAuth()"/>
    <div class="login-step-bg"></div>
  </div>
  <template v-else-if="activeStep === 3">
      <div  class="wizard-view wizard-view--2">
        <div class="wizard-view-header">
          <a href="/" class="wizard-logo-icon"></a>
          <div class="start-intro-btn-wrap" @click="showIntro(true);">
            <font-awesome-icon :icon="['fas', 'play']" class="start-intro-btn-wrap__icon"/>
            {{$t('becomeManager.lifeAtPeoplePolitics')}}
          </div>
          <font-awesome-icon :icon="['fas', 'right-from-bracket']"
                             class="wizard-logout-btn"
                             @click="showLogoutConfirmDialog()"
          />
        </div>
        <bank-id-form :is-become-manager-form="true" :hide-cancel-control="true"/>
      </div>
  </template>
  <div v-else-if="activeStep === 4" class="wizard-view wizard-view--3">
    <div class="wizard-view-header">
      <a href="/" class="wizard-logo-icon"></a>
      <div class="start-intro-btn-wrap" @click="showIntro(true);">
        <font-awesome-icon :icon="['fas', 'play']" class="start-intro-btn-wrap__icon"/>
        {{$t('becomeManager.lifeAtPeoplePolitics')}}
      </div>
      <font-awesome-icon :icon="['fas', 'right-from-bracket']"
                         class="wizard-logout-btn"
                         @click="showLogoutConfirmDialog()"
      />
    </div>
    <div class="wizard-form-wrap">
        <h3 class="form-section-title">{{$t('becomeManager.form.basicInformation')}}</h3>
        <div class="form-group">
          <label>{{$t('becomeManager.form.firstName')}}</label>
          <InputText type="text"
                     v-model="activeProfileInfo.firstName"
                     :placeholder="$t('becomeManager.form.yourFirstName')"
                     disabled="disabled"
          />
        </div>
        <div class="form-group">
          <label>{{$t('becomeManager.form.lastName')}}</label>
          <InputText type="text"
                     v-model="activeProfileInfo.lastName"
                     :placeholder="$t('becomeManager.form.yourLastName')"
                     disabled="disabled"
          />
        </div>
        <div class="form-group">
          <label>{{$t('becomeManager.form.email')}}</label>
          <InputText type="text"
                     v-model="userEmails[0].email"
                     :placeholder="$t('becomeManager.form.yourEmail')"
                     disabled="disabled"
          />
        </div>
        <div class="form-group">
          <label>{{$t('becomeManager.form.phone')}}</label>
          <InputText v-model="phoneNumber"
                     type="text"
                     :class="{'error': v$.phoneNumber.$error}"
                     :maxlength="20"
                     @keypress="isNumber($event)"
                     :placeholder="$t('becomeManager.form.pleaseEnterYour')"
                     autofocus
          />
          <template v-if="v$.phoneNumber.$error">
            <span v-for="error of v$.phoneNumber.$errors" :key="error.$uid" class="validationMessage">
              {{ error.$message }}
            </span>
          </template>
        </div>
        <h3 class="form-section-title">{{$t('becomeManager.form.whyDoYouWantThisJobTitle')}}</h3>
        <div class="form-group">
          <label> {{$t('becomeManager.form.whyDoYouWantThisJob')}}</label>
          <Textarea v-model="applicationText"
                    :maxlength="4000"
                    :placeholder="$t('becomeManager.form.describeWhyDoYouWantThisJob')"
                    :class="{'error': v$.applicationText.$error}"
          />
          <template v-if="v$.applicationText.$error">
            <span v-for="error of v$.applicationText.$errors" :key="error.$uid" class="validationMessage">
              {{ error.$message }}
            </span>
          </template>
        </div>
        <div class="form-group">
          <label>{{$t('becomeManager.form.briefDescriptionAboutYourself')}}</label>
          <Textarea v-model="aboutMe"
                    :maxlength="4000"
                    :placeholder="$t('becomeManager.form.briefDescriptionAboutYourself')"
                    :class="{'error': v$.aboutMe.$error}"
          />
          <template v-if="v$.aboutMe.$error">
                    <span v-for="error of v$.aboutMe.$errors" :key="error.$uid" class="validationMessage">
                      {{ error.$message }}
                  </span>
          </template>
        </div>
      <div class="form-group mb-10">
        <label>{{$t('becomeManager.form.attachSocialMedia')}}</label>
      </div>
      <div class="form-group">
        <div class="social-link">
          <div class="social-link__icon social-link__icon--fb"></div>
          <input v-model="facebookProfile"
                 type="text"
                 maxlength="255"
                 :placeholder="$t('becomeManager.form.enterFacebookProfile')"
                 class="bm-form-field facebookProfile"
                 :class="{'form-input--error': v$.facebookProfile.$error}"
          >
        </div>
        <template v-if="v$.facebookProfile.$error">
          <span v-for="error of v$.facebookProfile.$errors" :key="error.$uid"
                class="validationMessage validationMessage--social-link"
          >
            {{ error.$message }}
          </span>
        </template>
      </div>
      <div class="form-group">
        <div class="social-link">
          <div class="social-link__icon social-link__icon--linkedin"></div>
          <input v-model="linkedinProfile"
                 type="text"
                 maxlength="255"
                 :placeholder="$t('becomeManager.form.enterLinkedinProfile')"
                 class="bm-form-field"
                 :class="{'form-input--error': v$.linkedinProfile.$error}"
          />
        </div>
        <template v-if="v$.linkedinProfile.$error">
          <span v-for="error of v$.linkedinProfile.$errors" :key="error.$uid"
                class="validationMessage validationMessage--social-link"
          >
            {{ error.$message }}
          </span>
        </template>
      </div>
      <div class="form-group">
        <div class="social-link">
          <div class="social-link__icon social-link__icon--insta"></div>
          <input v-model="instagramProfile"
                 type="text"
                 maxlength="255"
                 :placeholder="$t('becomeManager.form.enterInstagramProfile')"
                 class="bm-form-field"
                 :class="{'form-input--error': v$.linkedinProfile.$error}"
          >
        </div>
        <template v-if="v$.instagramProfile.$error">
          <span v-for="error of v$.instagramProfile.$errors" :key="error.$uid"
                class="validationMessage validationMessage--social-link"
          >
            {{ error.$message }}
          </span>
        </template>
      </div>
      <div class="form-group">
        <div class="social-link">
          <div class="social-link__icon social-link__icon--snapchat"></div>
          <input v-model="snapchatProfile"
                 type="text"
                 maxlength="255"
                 :placeholder="$t('becomeManager.form.enterSnapchatProfile')"
                 class="bm-form-field"
                 :class="{'form-input--error': v$.linkedinProfile.$error}"
          >
        </div>
        <template v-if="v$.snapchatProfile.$error">
          <span v-for="error of v$.snapchatProfile.$errors" :key="error.$uid"
                class="validationMessage validationMessage--social-link"
          >
            {{ error.$message }}
          </span>
        </template>
      </div>
      <div class="form-group">
        <button type="button" @click="sendApplication()">{{$t('becomeManager.form.sendApplication')}}</button>
      </div>
    </div>
  </div>
  <div v-else-if="activeStep === 5" class="wizard-view wizard-view--4 final-step">
    <div class="wizard-view-header">
      <a href="/" class="wizard-logo-icon"></a>
      <div class="start-intro-btn-wrap" @click="showIntro(true);">
        <font-awesome-icon :icon="['fas', 'play']" class="start-intro-btn-wrap__icon"/>
        {{$t('becomeManager.lifeAtPeoplePolitics')}}
      </div>
      <font-awesome-icon :icon="['fas', 'right-from-bracket']"
                         class="wizard-logout-btn"
                         @click="showLogoutConfirmDialog()"
      />
    </div>
    <template v-if="!userIsManager || !userIsActiveManager">
      <div class="final-step">
        <div class="final-step-title">
          <i class="fa fa-check final-step-title__icon" aria-hidden="true"></i>
          <h3 class="final-step-title__text">
            {{$t('becomeManager.form.thankForApplication')}}
          </h3>
        </div>
        <span class="final-step__desc">
          {{$t('becomeManager.form.weWillReviewYourApplication')}}
        </span>
      </div>
    </template>
    <template v-else>
      <div class="final-step">
        <div class="final-step-title">
          <i class="fa fa-check final-step-title__icon" aria-hidden="true"></i>
          <div>
            <h3 class="final-step-title__text">{{$t('becomeManager.form.youAreAlreadyManager')}}</h3>
            <span class="final-step__desc">({{$t('becomeManager.form.noNeedGorYouToApply')}} <i class="smile-icon ml-5"></i>)</span>
          </div>
        </div>
      </div>
    </template>
  </div>
  <div v-if="showIntroState" class="intro-video-wrap">
    <video id="introVideo" class="intro-video" controls preload="auto" autoplay="true" tabindex="-1" role="application" src="https://d3lhb68qeo1nf6.cloudfront.net/video/life_at_people_politics_MASTER_720.mp4" data-setup="{}">
      <source src="https://d3lhb68qeo1nf6.cloudfront.net/video/life_at_people_politics_MASTER_720.mp4" type="video/mp4">
      <param name="allowfullscreen" value="true">
    </video>
    <font-awesome-icon :icon="['fas', 'times']"
                       class="close-intro-video"
                       @click="hideIntro()"
    />
  </div>

  <Dialog v-model:visible="logoutModalState"
          class="reset-password-modal"
          modal
          :header="$t('logoutConfirm.title')"
  >
    <div>
      {{$t('logoutConfirm.text')}}
    </div>
    <template #footer>
      <button type="button" class="p-dialog-close-btn" @click="logoutModalState = false">
        {{$t('common.buttons.cancel')}}
      </button>
      <button class="p-dialog-primary-btn" @click="logout()">
        {{$t('common.buttons.restart')}}
      </button>
    </template>
  </Dialog>
</template>

<style lang="scss">
.start-intro-btn-wrap,
.start-intro-btn {
  z-index: 9;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0 15px;
  font-size: 12px;
  text-align: center;
  font-weight: 600;
  line-height: 100px;
  color: rgba(255, 255, 255, 0.9);
  border-radius: 5px;
  background: linear-gradient(-45deg, #672992, #a163cd, #7835a6);
  background-size: 600%;
  -webkit-animation: anime 5s linear infinite;
  animation: anime 5s linear infinite;

  &__icon {
    margin-right: 5px;
  }
}

.start-intro-btn {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  filter: blur(30px);
  opacity: 0.8;
}

@-webkit-keyframes anime {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

@keyframes anime {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

.intro-video-wrap {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100vw;
  height: 100vh;
  display: flex;
  background: #000;

  video {
    width: 100%;
  }

  .close-intro-video {
    position: absolute;
    top: 0;
    right: 0;
    color: #fff;
    font-size: 18px;
    padding: 15px;
  }
}
.wizard-view-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  width: 100%;
  position: relative;
  top: 10px;
  z-index: 1;
  font-size: 16px;
  font-weight: 600;
  color: #fff;

  .wizard-logo-icon {
    display: block;
    width: 35px;
    height: 35px;
    background: url("~@/assets/mobile/pp-logo-purple.png"), no-repeat, center;
    background-size: 100%;
    border-radius: 25px;
  }

  .wizard-logout-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    background: #7835A6;
    padding: 10px;
    border-radius: 5px;
  }
}

.wizard-view {
  min-height: 100vh;
  padding: 0 20px;

  .login-step-bg {
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.18));
  }

  .step-title {
    position: relative;
    z-index: 1;
    margin-bottom: 30px;
    font-family: 'Open Sans',Arial,sans-serif;
    font-size: 21px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.33;
    letter-spacing: normal;
    color: #fff;
  }

  .step-desc-section {
    margin-bottom: 47px;
  }

  .step-desc {
    position: relative;
    z-index: 1;
    margin-bottom: 10px;
    font-family: 'Open Sans',Arial,sans-serif;
    font-size: 16px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.38;
    letter-spacing: normal;
    color: #fff;
  }

  &--1 {
    background: url("~@/assets/mobile/log-in-bg-image.png"), no-repeat, center;
    background-size: cover;

    .wizard-view-header {
      margin-bottom: 70px;
    }

    .login-btn {
      display: flex;
      align-items: center;
      width: 100%;
      height: 40px;
      margin: 0;
      position: relative;
      z-index: 1;
      font-family: 'Open Sans',Arial,sans-serif;
      font-size: 15px;
      border-radius: 6px;
      background-color: #a163cd;
    }
  }

  &--2 {
    .wizard-view-header {
      margin-bottom: 70px;
    }

    .bank-id-auth-form {
      padding: 20px;
    }
  }

  &--3 {
    .wizard-form-wrap {
      padding: 20px 0;
    }

    .wizard-view-header {
      margin-bottom: 20px;
    }

    .social-link {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 10px;

      &__icon {
        width: 30px;
        height: 30px;
        margin-right: 5px;
        background-size: 100%;
        background-repeat: no-repeat;

        &--fb {
          background: url("~@/assets/img/become-manager/social/f-logo-rgb-blue-58@3x.png") center/100% no-repeat;
        }
        &--linkedin {
          background: url("~@/assets/img/become-manager/social/li-in-bug@3x.png") center/100% no-repeat;
        }
        &--insta {
          background: url("~@/assets/img/become-manager/social/insta-icon.png") center/100% no-repeat;
        }
        &--snapchat {
          background: url("~@/assets/img/become-manager/social/snapchat-icon.png") center/100% no-repeat;
        }
      }
    }
  }

  &--4 {
    .wizard-view-header {
      position: sticky;
      margin-bottom: 130px;
    }

    .final-step {
      .final-step-title {
        display: flex;
        justify-content: center;
        margin-bottom: 12px;
        &__icon {
          margin: 5px 15px 0 0;
          font-size: 30px;
          color: #94ea6e
        }
        &__text {
          margin-bottom: 10px;
          font-family: 'Open Sans', Arial, sans-serif;
          font-style: normal;
          font-weight: 700;
          font-size: 20px;
          color: #4f4f4f;
        }
      }

      &__desc {
        display: flex;
        align-items: center;
        font-size: 14px;
        line-height: 18px;
        color: #3c3c3c;
      }
    }
  }
}

.form-section-title {
  margin-bottom: 15px;
  color: #4f4f4f;
  font-size: 24px;
  font-weight: 700;
}

.smile-icon {
  display: inline-block;
  width: 24px;
  height: 24px;
  margin-left: 10px;
  margin-right: 3px;
  background: url("~@/assets/img/opinion/pretty_good.svg") no-repeat;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;

  label {
    margin-bottom: 5px;
    font-size: 14px;
    font-weight: 600;
    color: #4f4f4f;
  }

  input,
  textarea {
    min-height: 2.375rem;
    width: 100%;
    padding: 12px;
    border-radius: 4px;
    border: 1px solid #d1d1d1;
    background: #fff;
    color: #4f4f4f;

    &::placeholder {
      color: #bbbbbb;
    }
    &:focus {
      outline-color: #a163cc !important;
    }
    &.error {
      border: 1px solid red !important;
    }
  }

  .p-inputtext {
    &:disabled {
      background: #d1d1d1;
      color: rgba(0, 0, 0, 0.4);
    }
    &.error {
      border: 1px solid red !important;
    }
  }

  .validationMessage {
    margin-top: 5px;
    color: red;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 55px;
    margin-bottom: 20px;
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    border-radius: 8px;
    border: none;
    background: #7835a6;
    cursor: pointer;
  }
}

.reset-password-modal {
  .p-dialog-content {
    background: #fff;
  }
}

</style>